import type { AbortSignal as SdkAbortSignal } from "@aws-sdk/types";
import {
  ListInternationalProductLiabilityClaimsCommand,
  ListInternationalProductLiabilityClaimsInput,
  ListInternationalProductLiabilityClaimsOutput,
} from "@amzn/ttechclaimintakeservice-client";

import { getAuthenticationToken } from "../../auth/helpers";
import { setupClaimIntakeClient } from "../setup/setupClaimIntakeClient";
import { MetricsPublisher } from "../../metrics/metrics";

/**
 * Lists International Product Liability claims.
 *
 * An optional `AbortSignal` cancels the in-flight request so a superseded or
 * timed-out search stops consuming a Lambda invocation instead of running to
 * completion. When aborted, the `AbortError` is re-thrown so callers can tell
 * a cancellation apart from a real service failure.
 */
export async function listIPLClaimsService(
  input: ListInternationalProductLiabilityClaimsInput,
  signal?: AbortSignal
) {
  const token = await getAuthenticationToken();
  if (!token) {
    return null;
  }
  const client = setupClaimIntakeClient(token);
  const command = new ListInternationalProductLiabilityClaimsCommand(input);
  const metricsPublisher = new MetricsPublisher("api.listIPLClaims");

  return await client
    .send(command, { abortSignal: signal as unknown as SdkAbortSignal })
    .then((response: ListInternationalProductLiabilityClaimsOutput) => {
      metricsPublisher.publishAuthenticationLatencyMetrics();
      metricsPublisher.publishRenderSuccessMetrics();
      return response;
    })
    .catch((error: unknown) => {
      // A cancellation is caller-initiated, so publishing a failure metric here
      // would report an operational problem that did not happen.
      if (error instanceof Error && error.name === "AbortError") {
        throw error;
      }
      metricsPublisher.publishAuthenticationLatencyMetrics();
      metricsPublisher.publishRenderFatalMetrics();
      throw error;
    });
}
